import { ThemeOptions } from '@mui/material';
import MuiCheckbox, {
    CheckboxProps as MuiCheckboxProps
} from '@mui/material/Checkbox';
import { FormControlLabelProps as MuiFormControlLabelProps } from '@mui/material/FormControlLabel';
import MuiFormGroup from '@mui/material/FormGroup';
import MuiFormHelperText from '@mui/material/FormHelperText';
import * as React from 'react';

import { FormControlLabel } from '../form-control';
import { FieldSkeleton } from '../textfield/utils';
import { firstLetterUppercase, sentence, Typography } from '../typography';
import { BaseProps, enumType, makeEnum, withVersion } from '../utils';
import { CheckboxGroupContext } from './context';

export { CheckboxGroupContext } from './context';

const sizes = ['small', 'medium', 'large'] satisfies MuiCheckboxProps['size'][];
export const CheckboxSize = makeEnum(...sizes);
export type CheckboxSize = enumType<typeof CheckboxSize>;

export type CheckboxProps = TypedOmit<MuiCheckboxProps, 'onChange' | 'color'> &
    BaseProps & {
        label?: React.ReactNode;
        error?: React.ReactNode;
        helperText?: string;
        labelProps?: Partial<MuiFormControlLabelProps>;
        skeleton?: boolean;
        onChange?(
            checked: boolean,
            event: React.ChangeEvent<HTMLInputElement>
        ): void;
    };

export const Checkbox = withVersion((props: CheckboxProps) => {
    const {
        label,
        labelProps,
        error,
        onChange,
        helperText,
        skeleton = false,
        ...muiProps
    } = props;
    const inGroup = React.useContext(CheckboxGroupContext);
    const control = (
        <MuiCheckbox
            onChange={(e) => onChange?.(e.target.checked, e)}
            {...muiProps}
        />
    );

    const wrapSkeleton = (content: React.ReactNode) =>
        skeleton ? (
            <FieldSkeleton width="fit-content">{content}</FieldSkeleton>
        ) : (
            content
        );

    if (!label) {
        return wrapSkeleton(control);
    }

    const labelNode = (
        <>
            <FormControlLabel
                {...labelProps}
                control={control}
                label={
                    <Typography
                        textTransform="sentence"
                        color={muiProps.disabled ? 'text.disabled' : undefined}
                    >
                        {label}
                    </Typography>
                }
                disableTypography
            />
            {error ? (
                <MuiFormHelperText error>{sentence(error)}</MuiFormHelperText>
            ) : (
                helperText && (
                    <MuiFormHelperText sx={{ ...firstLetterUppercase }}>
                        {helperText}
                    </MuiFormHelperText>
                )
            )}
        </>
    );

    if (inGroup) {
        return wrapSkeleton(labelNode);
    }

    return wrapSkeleton(<MuiFormGroup>{labelNode}</MuiFormGroup>);
}, '1.0.0');

export { CheckboxGroup } from './CheckboxGroup';
export type { CheckboxGroupProps } from './CheckboxGroup';

export const checkboxStyle = {
    MuiCheckbox: {
        styleOverrides: {
            root: {
                '& input[type=checkbox]': {
                    marginTop: 0
                }
            }
        }
    }
} satisfies ThemeOptions['components'];
